import { Timeline, WhatsApp } from "@mui/icons-material";
import { alpha, Box, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { READABLE_BODY_SX, READABLE_SECONDARY_SX } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";

type ActionKeywordVariant = "whatsapp" | "evolve";

const ACTION_CHIP_COLORS: Record<ActionKeywordVariant, string> = {
  whatsapp: "#25D366",
  evolve: "#9c27b0",
};

export function InsightActionKeyword({
  icon,
  label,
  variant,
}: {
  icon: ReactNode;
  label: string;
  variant: ActionKeywordVariant;
}) {
  const color = ACTION_CHIP_COLORS[variant];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.65,
        mx: 0.35,
        px: 1.1,
        py: 0.45,
        borderRadius: 1,
        border: "1px solid",
        borderColor: alpha(color, 0.45),
        bgcolor: alpha(color, 0.12),
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        sx={{ display: "inline-flex", color, "& svg": { fontSize: 22 } }}
      >
        {icon}
      </Box>
      <Typography
        component="span"
        fontWeight={700}
        sx={{ ...READABLE_BODY_SX, color: "text.primary", lineHeight: 1.25 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const guideParagraphSx = {
  m: 0,
  ...READABLE_SECONDARY_SX,
  lineHeight: 1.65,
} as const;

export function insightActionGuideBoxSx(accentColor: string) {
  return {
    px: 1.75,
    py: 1.35,
    borderRadius: 1,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: alpha(accentColor, 0.28),
    borderLeft: `3px solid ${accentColor}`,
  } as const;
}

export function InsightFrameGuide({ text }: { text: string }) {
  const evolve = <InsightActionKeyword icon={<Timeline />} label="Evoluir" variant="evolve" />;
  const whatsApp = (
    <InsightActionKeyword icon={<WhatsApp />} label="WhatsApp" variant="whatsapp" />
  );

  const hasEvolve = text.toLowerCase().includes("evoluir");
  const hasWhatsApp = text.toLowerCase().includes("whatsapp");

  if (!hasEvolve && !hasWhatsApp) {
    return (
      <Typography component="p" sx={guideParagraphSx}>
        {text}
      </Typography>
    );
  }

  const parts = text.split(/(Evoluir|WhatsApp)/i);
  return (
    <Typography component="p" sx={guideParagraphSx}>
      {parts.map((part, index) => {
        if (part.toLowerCase() === "evoluir") return <span key={index}>{evolve}</span>;
        if (part.toLowerCase() === "whatsapp") return <span key={index}>{whatsApp}</span>;
        return <span key={index}>{part}</span>;
      })}
    </Typography>
  );
}

/** @deprecated Use InsightFrameGuide */
export function InsightConductFrameGuide() {
  return (
    <InsightFrameGuide text="Escolha situação ou conduta para ver os pacientes. Depois use Evoluir ou mande WhatsApp." />
  );
}
